<template>
  <modal
    v-if="isVisible"
    elem-id="modal_lgpd_termo"
    modal-size="modal-lg"
    :title="editingId ? t('Editar termo LGPD') : t('Cadastrar termo LGPD')"
    :show="isVisible"
    :close-label="t('Cancelar')"
    close-test-id="lgpd-termo-modal-cancel-btn"
    :save-label="isSaving ? t('Salvando') : t('Salvar')"
    save-icon="pi pi-check"
    save-test-id="lgpd-termo-modal-save-btn"
    :save-fn="handleSaveClick"
    :save-disabled-fn="() => isSaving"
    @close="closeModal"
    @dismiss="closeModal"
    data-testid="lgpd-termo-modal"
  >
    <div v-if="isLoadingTerm" class="loading-container" data-testid="lgpd-termo-modal-loading">
      <spinner style="width: 32px; height: 32px;" />
    </div>
    <template v-else>
      <lgpd-termo-form
        ref="formRef"
        v-model="formData"
      />

      <div class="form-extra" v-if="!isLoadingTerm">
        <div class="form-group">
          <label class="toggle-label">
            <Toggle
              v-model="activateAfterSave"
              :disabled="isCurrentlyActive"
              data-testid="lgpd-termo-modal-activate-toggle"
            />
            <span>{{ t('Ativar este termo após salvar') }}</span>
          </label>
          <small v-if="isCurrentlyActive" class="toggle-hint">
            {{ t('Este termo já está ativo.') }}
          </small>
        </div>
      </div>

      <div v-if="deleteError" class="error-message" data-testid="lgpd-termo-modal-delete-error">
        <i class="pi pi-exclamation-triangle"></i>
        {{ deleteError }}
      </div>

      <!-- Danger zone (edit mode only) — destructive action lives in the body,
           not the footer, because Modal.vue's footer reserves close + save only. -->
      <div v-if="editingId" class="danger-zone">
        <Button
          :label="t('Excluir termo')"
          icon="pi pi-trash"
          severity="danger"
          size="small"
          outlined
          :disabled="isSaving"
          data-testid="lgpd-termo-modal-delete-btn"
          @click="handleDelete"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { useLgpdAdmin } from '../../composables/useLgpdAdmin';
import LgpdTermoForm from './LgpdTermoForm.vue';

const { t } = useI18n();
const { confirmAction } = useConfirmAction();
const { listTerms, saveTerm, activateTerm, deleteTerm } = useLgpdAdmin();

const isVisible = ref(false);
const editingId = ref<number | null>(null);
const isSaving = ref(false);
const isLoadingTerm = ref(false);
const activateAfterSave = ref(false);
const isCurrentlyActive = ref(false);
const deleteError = ref<string | null>(null);

const formRef = ref<InstanceType<typeof LgpdTermoForm> | null>(null);

const formData = ref<{ titulo: string; versao: string; conteudo: string }>({
  titulo: '',
  versao: '',
  conteudo: '',
});

const closeModal = (): void => {
  log('LgpdTermoModal', 'closeModal', 'Closing');
  isVisible.value = false;
  editingId.value = null;
  activateAfterSave.value = false;
  isCurrentlyActive.value = false;
  deleteError.value = null;
};

const loadTermForEdit = async (id: number): Promise<void> => {
  log('LgpdTermoModal', 'loadTermForEdit', 'Loading term', id);
  isLoadingTerm.value = true;
  try {
    const terms = await listTerms();
    const term = terms.find((t) => t.id === id);
    if (term) {
      formData.value = {
        titulo: term.titulo,
        versao: term.versao,
        conteudo: term.conteudo,
      };
      isCurrentlyActive.value = term.ativo === 1;
    }
  } catch (err) {
    log('LgpdTermoModal', 'loadTermForEdit', 'Error', err);
  } finally {
    isLoadingTerm.value = false;
  }
};

const handleSave = async (data: { titulo: string; versao: string; conteudo: string }): Promise<void> => {
  log('LgpdTermoModal', 'handleSave', 'Saving term', data);
  isSaving.value = true;
  deleteError.value = null;

  try {
    const payload: { titulo: string; versao: string; conteudo: string; id?: number; ativo?: boolean } = {
      ...data,
      id: editingId.value ?? undefined,
    };
    if (activateAfterSave.value && !isCurrentlyActive.value) {
      payload.ativo = true;
    }

    const termId = await saveTerm(payload);

    if (activateAfterSave.value && !isCurrentlyActive.value) {
      await activateTerm(termId);
    }

    eventBus.emit(editingId.value ? 'lgpd_term.updated' : 'lgpd_term.created');
    eventBus.emit('toast.show', {
      severity: 'success',
      summary: t('Sucesso'),
      detail: editingId.value ? t('Termo atualizado') : t('Termo cadastrado'),
    });
    closeModal();
  } catch (err) {
    log('LgpdTermoModal', 'handleSave', 'Error', err);
  } finally {
    isSaving.value = false;
  }
};

const handleSaveClick = (): void => {
  // Trigger form submit via the form component's emit
  if (formRef.value) {
    const data = {
      titulo: formData.value.titulo,
      versao: formData.value.versao,
      conteudo: formData.value.conteudo,
    };
    handleSave(data);
  }
};

const handleDelete = (): void => {
  log('LgpdTermoModal', 'handleDelete', 'Prompting delete confirmation');
  confirmAction({
    message: t('Tem certeza que deseja excluir este termo LGPD?'),
    header: t('Confirmar exclusão'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim, excluir'),
    rejectLabel: t('Cancelar'),
    onAccept: async () => {
      if (!editingId.value) return;
      deleteError.value = null;
      const result = await deleteTerm(editingId.value);
      if (result.ok) {
        eventBus.emit('lgpd_term.deleted');
        eventBus.emit('toast.show', {
          severity: 'success',
          summary: t('Sucesso'),
          detail: t('Termo excluído'),
        });
        closeModal();
      } else {
        deleteError.value = result.message;
      }
    },
  });
};

const handleOpen = (data?: { id?: number }): void => {
  log('LgpdTermoModal', 'handleOpen', 'Opening', data);
  formData.value = { titulo: '', versao: '', conteudo: '' };
  editingId.value = null;
  activateAfterSave.value = false;
  isCurrentlyActive.value = false;
  deleteError.value = null;

  if (data?.id) {
    editingId.value = data.id;
    loadTermForEdit(data.id);
  }
  isVisible.value = true;
};

onMounted(() => {
  eventBus.on('modal.lgpd-termo.open', handleOpen);
});

onBeforeUnmount(() => {
  eventBus.off('modal.lgpd-termo.open', handleOpen);
});
</script>

<style scoped>
.form-extra {
  padding: 12px 0;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
}

.toggle-hint {
  color: #9ca3af;
  font-size: 12px;
  margin-left: 36px;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  color: #b91c1c;
  font-size: 13px;
  margin-top: 8px;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.modal-footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.footer-right {
  display: flex;
  gap: 8px;
}
</style>
